import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { keyframes, cx, css } from 'emotion';
import ThemeContext from '@weave-design/theme-context';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

const MAX_INCREASE_PER_MS = 1 / 1000;

const ProgressRingDeterminateBehavior = props => {
  const [cssTransitionState, setCSSTransitionState] = useState(null);
  const [transitionEnter, setTransitionEnter] = useState(true);
  const containerRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  let value = 0;
  let prevTimestamp = 0;
  let targetValue;
  let segments;
  let SEGMENT_COUNT;
  let FADE_DELAY_FACTOR;

  const wait = () => {
    prevTimestamp = window.performance.now();

    if (!animationFrameIdRef.current) {
      // eslint-disable-next-line no-use-before-define
      animationFrameIdRef.current = window.requestAnimationFrame(step);
    }
  };

  const enter = () => {
    segments.forEach(segment => {
      const eachSegment = segment;
      eachSegment.style.opacity = 0;
    });
    setTransitionEnter(true);
    wait();
  };
  /**
   * @todo The exit function is associated with the final state of the animation.
   */


  const exit = () => {
    segments.forEach(segment => {
      const eachSegment = segment;
      eachSegment.style.opacity = null;
    });
    setTransitionEnter(false);
    wait();
  };

  const opacityForSegment = (index, param) => {
    const fadeStartValue = index * FADE_DELAY_FACTOR;
    return Math.max(0, (param - fadeStartValue) / FADE_DELAY_FACTOR);
  };

  const setSegmentOpacities = param => {
    segments.forEach((segment, i) => {
      const index = Math.abs(i - SEGMENT_COUNT) - 1;
      const eachSegment = segment;
      eachSegment.style.opacity = opacityForSegment(index, param);
    });
  };

  const progressTowardTarget = timestamp => {
    const elapsed = timestamp - prevTimestamp;
    let valueDiff;

    if (targetValue > value) {
      valueDiff = Math.min(elapsed * MAX_INCREASE_PER_MS, targetValue - value);
    } else if (targetValue < value) {
      valueDiff = Math.max(-elapsed * MAX_INCREASE_PER_MS, targetValue - value);
    } else {
      if (cssTransitionState !== "exited") {
        setSegmentOpacities(value);
      }

      prevTimestamp = undefined;
      return;
    }

    const interrumValue = value + valueDiff;
    setSegmentOpacities(interrumValue);
    prevTimestamp = timestamp;
    value = interrumValue;

    if (!animationFrameIdRef.current) {
      // eslint-disable-next-line no-use-before-define
      animationFrameIdRef.current = window.requestAnimationFrame(step);
    }
  };

  const step = timestamp => {
    animationFrameIdRef.current = undefined;

    if (cssTransitionState === "entering" || cssTransitionState === "exiting") {
      wait();
      return;
    }

    if (cssTransitionState === "exited" && value !== targetValue) {
      enter();
      return;
    }
    /**
     * @todo The condition associated with the final state of the animation was modified
     * to prevent it from running. The exited state of the animation is the origin of an
     * infinite loop. The component must be refactored to optimize the component life cycle.
     */


    if (targetValue === 1 && value === 1 && cssTransitionState === "exited") {
      exit();
      return;
    }

    progressTowardTarget(timestamp);
  };

  const setProgress = percent => {
    targetValue = percent / 100;

    if (!prevTimestamp) {
      prevTimestamp = window.performance.now();
    }

    animationFrameIdRef.current = window.requestAnimationFrame(step);
  };

  const initSegments = () => {
    const {
      current
    } = containerRef;
    const allSVGPathElements = Array.from(current.querySelectorAll("svg > path")); // remove the first svg path as that is the background

    segments = allSVGPathElements.filter((path, index) => index !== 0);
    SEGMENT_COUNT = segments.length;
    FADE_DELAY_FACTOR = 1 / SEGMENT_COUNT;
  };

  const handleEntering = () => {
    setCSSTransitionState("entering");
  };

  const handleEntered = () => {
    setCSSTransitionState("entered");
    setProgress(props.percentComplete);
  };

  const handleExiting = () => {
    setCSSTransitionState("exiting");
  };

  const handleExited = () => {
    setCSSTransitionState("exited");
  };

  useEffect(() => {
    if (cssTransitionState === "entered" || cssTransitionState === "exited") {
      initSegments();
      setProgress(props.percentComplete);
    }
  });
  useEffect(() => () => {
    if (animationFrameIdRef.current) {
      window.cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = undefined;
    }
  }, []);
  return /*#__PURE__*/React.createElement(CSSTransition, {
    in: transitionEnter,
    timeout: {
      enter: 650,
      exit: 466
    },
    appear: true,
    nodeRef: containerRef,
    onEntering: handleEntering,
    onEntered: handleEntered,
    onExiting: handleExiting,
    onExited: handleExited
  }, status => props.children({
    innerRef: containerRef,
    percentComplete: props.percentComplete,
    cssTransitionState: status
  }));
};

ProgressRingDeterminateBehavior.displayName = "ProgressRingDeterminateBehavior";
ProgressRingDeterminateBehavior.propTypes = {
  /**
   * An integer from 0 to 100 representing the percent the delayed operation has completed
   */
  percentComplete: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Render prop */
  children: PropTypes.func.isRequired
};
ProgressRingDeterminateBehavior.__docgenInfo = {
  "description": "",
  "displayName": "ProgressRingDeterminateBehavior",
  "props": {
    "percentComplete": {
      "type": {
        "name": "union",
        "value": [{
          "name": "string"
        }, {
          "name": "number"
        }]
      },
      "required": false,
      "description": "An integer from 0 to 100 representing the percent the delayed operation has completed"
    },
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": "Render prop"
    }
  }
};

const CYCLE_DURATION = 1000;
const FADE_DURATION = 416;

const ProgressRingIndeterminateBehavior = props => {
  const [cssTransitionState, setcssTransitionState] = useState(null);
  const [playing, setPlaying] = useState(null);
  const containerRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  let startTime;
  let segments;
  let SEGMENT_COUNT;
  let SEGMENT_DELAY_FACTOR;

  const opacityForSegment = (index, elapsedThisCycle) => {
    const segmentFadeStartTime = index * SEGMENT_DELAY_FACTOR; // Fade continuing from previous cycle

    if (segmentFadeStartTime + FADE_DURATION > CYCLE_DURATION && elapsedThisCycle < FADE_DURATION) {
      return ((elapsedThisCycle + CYCLE_DURATION - segmentFadeStartTime) / FADE_DURATION - 1) * -1;
    } // Fade has finished


    if (elapsedThisCycle < segmentFadeStartTime || elapsedThisCycle > segmentFadeStartTime + FADE_DURATION) {
      return 0;
    } // Fading


    return Math.abs((elapsedThisCycle - segmentFadeStartTime) / FADE_DURATION - 1);
  };

  const setSegmentOpacities = elapsedThisCycle => {
    segments.forEach((segment, i) => {
      const index = Math.abs(i - SEGMENT_COUNT) - 1;
      const eachSegment = segment;
      eachSegment.style.opacity = opacityForSegment(index, elapsedThisCycle);
    });
  };

  const initSegments = () => {
    const {
      current
    } = containerRef;
    const allSVGPathElements = Array.from(current.querySelectorAll("svg > path")); // remove the first svg path as that is the background

    segments = allSVGPathElements.filter((path, index) => index !== 0);
    SEGMENT_COUNT = segments.length;
    SEGMENT_DELAY_FACTOR = CYCLE_DURATION / SEGMENT_COUNT;
  };

  const step = timestamp => {
    animationFrameIdRef.current = undefined;

    if (!playing) {
      return;
    }

    if (cssTransitionState !== "entered" && !animationFrameIdRef.current) {
      animationFrameIdRef.current = window.requestAnimationFrame(step);
      return;
    }

    if (!startTime) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const elapsedThisCycle = elapsed % CYCLE_DURATION;
    setSegmentOpacities(elapsedThisCycle);

    if (!animationFrameIdRef.current) {
      animationFrameIdRef.current = window.requestAnimationFrame(step);
    }
  };

  const start = () => {
    setPlaying(true);
    startTime = undefined;

    if (!animationFrameIdRef.current) {
      animationFrameIdRef.current = window.requestAnimationFrame(step);
    }
  };

  const handleEntering = () => {
    setcssTransitionState("entering");
  };

  const handleEntered = () => {
    setcssTransitionState("entered");
    start();
  };

  const handleExiting = () => {
    setcssTransitionState("exited");
  };

  const handleExited = () => {
    setcssTransitionState("exited");
  };

  useEffect(() => {
    initSegments();
    step(1);
  });
  useEffect(() => () => {
    if (animationFrameIdRef.current) {
      window.cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = undefined;
    }
  }, []);
  return /*#__PURE__*/React.createElement(CSSTransition, {
    in: true,
    timeout: {
      enter: 650,
      exit: 466
    },
    appear: true,
    nodeRef: containerRef,
    onEntering: handleEntering,
    onEntered: handleEntered,
    onExiting: handleExiting,
    onExited: handleExited
  }, status => props.children({
    innerRef: containerRef,
    cssTransitionState: status
  }));
};

ProgressRingIndeterminateBehavior.displayName = "ProgressRingIndeterminateBehavior";
ProgressRingIndeterminateBehavior.propTypes = {
  /** Render prop */
  children: PropTypes.func.isRequired
};
ProgressRingIndeterminateBehavior.__docgenInfo = {
  "description": "",
  "displayName": "ProgressRingIndeterminateBehavior",
  "props": {
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": "Render prop"
    }
  }
};

const BackgroundEntering = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }

  61% {
    transform: scale(1.1);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const BackgroundExiting = color => keyframes`
    from {
      opacity: 1;
      transform: scale(1);
      fill: ${color};
    }

    10% {
      transform: scale(1.1);
    }

    60% {
      opacity: 1;
    }

    to {
      opacity: 0;
      transform: scale(0);
    }
  `;

const SegmentsExiting = keyframes`
  from {
    opacity: 1;
  }

  28% {
    opacity: 0;
  }
`;
function stylesheet(props, themeData) {
  const {
    cssTransitionState,
    stylesheet: customStylesheet
  } = props;
  const styles = {
    ring: {
      fontFamily: "ArtifaktElement",
      lineHeight: "1.6",
      boxSizing: "border-box",
      display: "flex",
      position: "relative",
      justifyContent: "center",
      alignItems: "center"
    },
    background: {
      transformOrigin: "center",
      fill: themeData["progress.ring.backgroundColor"]
    },
    segment: {
      opacity: 0,
      "&:nth-of-type(even)": {
        fill: themeData["progress.ring.highlightColor1"]
      },
      "&:nth-of-type(odd)": {
        fill: themeData["progress.ring.highlightColor2"]
      }
    }
  };

  if (cssTransitionState === "entering") {
    styles.background.animation = `${BackgroundEntering} 0.65s ease-in-out`;
  } else if (cssTransitionState === "exiting") {
    styles.background.animation = `${BackgroundExiting(themeData["progress.ring.highlightColor1"])} 0.466s ease-in forwards`;
    styles.segment.animation = `${SegmentsExiting} 0.466s linear`;
  } else if (cssTransitionState === "exited") {
    styles.background.opacity = 0;
    styles.background.transform = "scale(0)";
  }

  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

function SVGPresenter(props) {
  const {
    height,
    original,
    svgData,
    width
  } = props;

  const buildPaths = (svgs, resolvedRoles) => svgs.map((item, index) => {
    // the background is always the first svg path
    const styleKey = index === 0 ? "background" : "segment";
    const styles = stylesheet(props, resolvedRoles);
    return /*#__PURE__*/React.createElement("path", {
      key: item.d,
      className: cx(item.className, css(styles[styleKey])),
      fill: item.fill,
      fillRule: item.fillRule,
      d: item.d
    });
  });

  return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref => {
    let {
      resolvedRoles
    } = _ref;
    return /*#__PURE__*/React.createElement("svg", {
      width: width,
      height: height,
      viewBox: `0 0 ${original} ${original}`,
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg",
      xmlnsXlink: "http://www.w3.org/1999/xlink"
    }, buildPaths(svgData, resolvedRoles));
  });
}
SVGPresenter.propTypes = {
  height: PropTypes.number,
  original: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  svgData: PropTypes.arrayOf(PropTypes.object),
  width: PropTypes.number
};
SVGPresenter.__docgenInfo = {
  "description": "",
  "displayName": "SVGPresenter",
  "props": {
    "height": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": ""
    },
    "original": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": ""
    },
    "svgData": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "object"
        }
      },
      "required": false,
      "description": ""
    },
    "width": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": ""
    }
  }
};

const smallSVG = [{
  d: "M22.49 5.51 L14 2 L5.51 5.51 L2 14 L5.51 22.49 L14 26 L22.49 22.49 L26 14 ZM19.42 19.42 L14 21.67 L8.58 19.42 L6.33 14 L8.58 8.58 L14 6.33 L19.42 8.58 L21.67 14"
}, {
  fillRule: "nonzero",
  d: "M5.52 5.52 L14 6.33 L14 2"
}, {
  fillRule: "nonzero",
  d: "M5.52 5.52 L8.58 8.58 L14 6.33"
}, {
  fillRule: "nonzero",
  d: "M2 14 L8.58 8.58 L5.52 5.52"
}, {
  fillRule: "nonzero",
  d: "M2 14 L6.33 14 L8.58 8.58"
}, {
  fillRule: "nonzero",
  d: "M5.52 22.48 L6.33 14 L2 14"
}, {
  fillRule: "nonzero",
  d: "M5.52 22.48 L8.58 19.42 L6.33 14"
}, {
  fillRule: "nonzero",
  d: "M14 26 L8.58 19.42 L5.52 22.48"
}, {
  className: "hig__progress-ring__segment",
  fillRule: "nonzero",
  d: "M14 26 L14 21.67 L8.58 19.42"
}, {
  fillRule: "nonzero",
  d: "M22.48 22.48 L14 21.67 L14 26"
}, {
  fillRule: "nonzero",
  d: "M22.48 22.48 L19.42 19.42 L14 21.67"
}, {
  fillRule: "nonzero",
  d: "M26 14 L19.42 19.42 L22.48 22.48"
}, {
  fillRule: "nonzero",
  d: "M26 14 L21.67 14 L19.42 19.42"
}, {
  fillRule: "nonzero",
  d: "M22.48 5.52 L21.67 14 L26 14"
}, {
  fillRule: "nonzero",
  d: "M22.48 5.52 L19.42 8.58 L21.67 14"
}, {
  fillRule: "nonzero",
  d: "M14 2 L19.42 8.58 L22.48 5.52"
}, {
  fillRule: "nonzero",
  d: "M14 2 L14 6.33 L19.42 8.58"
}];

const mediumSVG = [{
  d:
  /* eslint-disable-next-line max-len */
  "M54.81 10.11 L36 4 L17.19 10.11 L5.57 26.11 L5.57 45.89 L17.19 61.89 L36 68 L54.81 61.89 L66.43 45.89 L66.43 26.11 ZM60.43 43.94 L51.1 56.78 L36 61.69 L20.9 56.78 L11.57 43.94 L11.57 28.06 L20.9 15.22 L36 10.31 L51.1 15.22 L60.43 28.07"
}, {
  fillRule: "nonzero",
  d: "M36 10.31 L36 4 L17.19 10.11"
}, {
  fillRule: "nonzero",
  d: "M36 10.31 L17.19 10.11 L20.9 15.215"
}, {
  fillRule: "nonzero",
  d: "M20.9 15.215 L17.19 10.11 L5.565 26.11"
}, {
  fillRule: "nonzero",
  d: "M20.9 15.215 L5.565 26.11 L11.565 28.06"
}, {
  fillRule: "nonzero",
  d: "M11.565 28.06 L5.565 26.11 L5.565 45.89"
}, {
  fillRule: "nonzero",
  d: "M11.565 28.06 L5.565 45.89 L11.565 43.94"
}, {
  fillRule: "nonzero",
  d: "M11.565 43.94 L5.565 45.89 L17.19 61.89"
}, {
  fillRule: "nonzero",
  d: "M11.565 43.94 L17.19 61.89 L20.9 56.785"
}, {
  fillRule: "nonzero",
  d: "M20.9 56.785 L17.19 61.89 L36 68"
}, {
  fillRule: "nonzero",
  d: "M20.9 56.785 L36 68 L36 61.69"
}, {
  className: "hig__progress-ring__segment",
  fillRule: "nonzero",
  d: "M36 61.69 L36 68 L54.81 61.89"
}, {
  fillRule: "nonzero",
  d: "M36 61.69 L54.81 61.89 L51.1 56.785"
}, {
  fillRule: "nonzero",
  d: "M51.1 56.785 L54.81 61.89 L66.435 45.89"
}, {
  fillRule: "nonzero",
  d: "M51.1 56.785 L66.435 45.89 L60.435 43.94"
}, {
  fillRule: "nonzero",
  d: "M60.435 43.94 L66.435 45.89 L66.435 26.11"
}, {
  fillRule: "nonzero",
  d: "M60.435 43.94 L66.435 26.11 L60.435 28.06"
}, {
  fillRule: "nonzero",
  d: "M60.435 28.06 L66.435 26.11 L54.81 10.11"
}, {
  fillRule: "nonzero",
  d: "M60.435 28.06 L54.81 10.11 L51.1 15.215"
}, {
  fillRule: "nonzero",
  d: "M51.1 15.215 L54.81 10.11 L36 4"
}, {
  fillRule: "nonzero",
  d: "M51.1 15.215 L36 4 L36 10.31"
}];

const xsmallSVG = [{
  fillRule: "nonzero",
  d: "M10 2 L3.07 6 L3.07 14 L10 18 L16.93 14 L16.93 6 ZM13.93 12.27 L10 14.54 L6.07 12.27 L6.07 7.73 L10 5.46 L13.93 7.73"
}, {
  fillRule: "nonzero",
  d: "M10 5.46 L10 2 L3.07 6"
}, {
  fillRule: "nonzero",
  d: "M6.07 7.73 L10 5.46 L3.07 6"
}, {
  fillRule: "nonzero",
  d: "M6.07 7.73 L3.07 6 L3.07 14"
}, {
  fillRule: "nonzero",
  d: "M6.07 12.27 L6.07 7.73 L3.07 14"
}, {
  fillRule: "nonzero",
  d: "M6.07 12.27 L3.07 14 L10 18"
}, {
  fillRule: "nonzero",
  d: "M10 14.54 L6.07 12.27 L10 18"
}, {
  fillRule: "nonzero",
  d: "M10 14.54 L10 18 L16.93 14"
}, {
  fillRule: "nonzero",
  d: "M13.93 12.27 L10 14.54 L16.93 14"
}, {
  fillRule: "nonzero",
  d: "M13.93 12.27 L16.93 14 L16.93 6"
}, {
  fillRule: "nonzero",
  d: "M13.93 7.73 L13.93 12.27 L16.93 6"
}, {
  fillRule: "nonzero",
  d: "M13.93 7.73 L16.93 6 L10 2"
}, {
  fillRule: "nonzero",
  d: "M10 5.46 L13.93 7.73 L10 2"
}];

/* eslint-disable import/prefer-default-export */
const availableSizes = Object.freeze(["xs", "s", "m", "l", "xl"]);

const _excluded$1 = ["innerRef", "percentComplete", "size", "cssTransitionState"];
const sizes = {
  xs: {
    svg: xsmallSVG,
    size: 20
  },
  s: {
    svg: smallSVG,
    size: 28
  },
  m: {
    svg: mediumSVG,
    size: 72
  },
  l: {
    svg: mediumSVG,
    size: 144
  },
  xl: {
    svg: mediumSVG,
    size: 242
  }
};
function ProgressRingPresenter(props) {
  const {
    innerRef,
    percentComplete,
    size,
    cssTransitionState
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded$1);

  const {
    className
  } = otherProps;
  const SVG = sizes[size].svg;
  const originalSize = sizes[size].svg === mediumSVG ? 72 : sizes[size].size;
  return /*#__PURE__*/React.createElement("div", {
    className: cx([className, css(stylesheet(props, {}).ring)]),
    role: "progressbar",
    "aria-valuemin": "0",
    "aria-valuemax": "100",
    "aria-valuenow": percentComplete,
    ref: innerRef
  }, /*#__PURE__*/React.createElement(SVGPresenter, _extends({
    height: sizes[size].size,
    width: sizes[size].size,
    original: originalSize,
    svgData: SVG,
    cssTransitionState: cssTransitionState
  }, otherProps)));
}
ProgressRingPresenter.defaultProps = {
  size: "m"
};
ProgressRingPresenter.propTypes = {
  cssTransitionState: PropTypes.string,
  innerRef: PropTypes.shape({
    current: PropTypes.shape([PropTypes.any])
  }),
  percentComplete: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(availableSizes),
  stylesheet: PropTypes.func
};
ProgressRingPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ProgressRingPresenter",
  "props": {
    "cssTransitionState": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "innerRef": {
      "type": {
        "name": "shape",
        "value": {
          "current": {
            "name": "shape",
            "value": "[PropTypes.any]",
            "computed": true,
            "required": false
          }
        }
      },
      "required": false,
      "description": ""
    },
    "percentComplete": {
      "type": {
        "name": "union",
        "value": [{
          "name": "string"
        }, {
          "name": "number"
        }]
      },
      "required": false,
      "description": ""
    },
    "size": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableSizes"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"m\"",
        "computed": false
      }
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

const _excluded = ["percentComplete", "size", "stylesheet"];

const ProgressRing = props => {
  const {
    percentComplete,
    size,
    stylesheet
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  const ProgressRingBehavior = percentComplete === undefined ? ProgressRingIndeterminateBehavior : ProgressRingDeterminateBehavior;
  const behaviorProps = percentComplete === undefined ? {} : {
    percentComplete
  };
  return /*#__PURE__*/React.createElement(ProgressRingBehavior, behaviorProps, _ref => {
    let {
      innerRef,
      cssTransitionState
    } = _ref;
    return /*#__PURE__*/React.createElement(ProgressRingPresenter, _extends({
      innerRef: innerRef,
      percentComplete: percentComplete,
      size: size,
      cssTransitionState: cssTransitionState,
      stylesheet: stylesheet
    }, otherProps));
  });
};

ProgressRing.propTypes = {
  /**
   * An integer from 0 to 100 representing the percent the delayed operation has completed.
   * If left blank or this prop is omitted you will have the indeterminate progress ring
   */
  percentComplete: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * {xs, s, m, l, xl} the size of the progress indicator
   */
  size: PropTypes.oneOf(availableSizes),

  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func
};
ProgressRing.defaultProps = {
  size: "m"
};
ProgressRing.__docgenInfo = {
  "description": "",
  "displayName": "ProgressRing",
  "props": {
    "percentComplete": {
      "type": {
        "name": "union",
        "value": [{
          "name": "string"
        }, {
          "name": "number"
        }]
      },
      "required": false,
      "description": "An integer from 0 to 100 representing the percent the delayed operation has completed.\nIf left blank or this prop is omitted you will have the indeterminate progress ring"
    },
    "size": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableSizes"
      },
      "required": false,
      "description": "{xs, s, m, l, xl} the size of the progress indicator",
      "defaultValue": {
        "value": "\"m\"",
        "computed": false
      }
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    }
  }
};

export { ProgressRing as default };
